import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { PARKS_CONFIG, normalizeName, buildLookup } from '../utils/waitTimesUtils';

const WaitTimesContext = createContext();

const API_BASE = 'https://api.themeparks.wiki/v1';
const STORAGE_KEY = 'dlp-wait-times-cache';
const BASE_INTERVAL = 60_000;      // 60s normal polling
const HIDDEN_INTERVAL = 300_000;   // 5 min when tab is hidden (save API calls)
const MAX_BACKOFF = 300_000;       // 5 min max backoff on repeated errors

export function useWaitTimes() {
  return useContext(WaitTimesContext);
}

export function WaitTimesProvider({ children }) {
  const [parksData, setParksData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [waitLookup, setWaitLookup] = useState({});

  // Refs for deduplication, abort, and backoff
  const fetchInFlight = useRef(false);
  const abortRef = useRef(null);
  const consecutiveErrors = useRef(0);
  const intervalRef = useRef(null);

  // Hydrate from localStorage on mount for instant display
  useEffect(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        setParksData(data);
        setWaitLookup(buildLookup(data));
        setLastFetch(new Date(timestamp));
      }
    } catch {
      // Corrupted cache — ignore
    }
  }, []);

  const fetchData = useCallback(async (isRefresh = false) => {
    // ── Request deduplication: skip if already in-flight ──
    if (fetchInFlight.current && !isRefresh) return;
    fetchInFlight.current = true;

    // ── Abort any previous in-flight request ──
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const results = await Promise.all(
        PARKS_CONFIG.map(park =>
          fetch(`${API_BASE}/entity/${park.id}/live`, {
            signal: controller.signal,
          }).then(res => {
            if (!res.ok) throw new Error(`API returned ${res.status}`);
            return res.json();
          })
        )
      );

      const data = {};
      PARKS_CONFIG.forEach((park, i) => {
        data[park.id] = results[i];
      });

      setParksData(data);
      setWaitLookup(buildLookup(data));
      setLastFetch(new Date());

      // Reset backoff on success
      consecutiveErrors.current = 0;

      // Persist to localStorage for offline fallback
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data,
          timestamp: new Date().toISOString(),
        }));
      } catch {
        // localStorage full or unavailable — silently ignore
      }

    } catch (err) {
      // Ignore aborted requests
      if (err.name === 'AbortError') return;

      // Increment backoff counter
      consecutiveErrors.current = Math.min(consecutiveErrors.current + 1, 5);

      // Network + SW cache both failed — try localStorage
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          setParksData(data);
          setWaitLookup(buildLookup(data));
          setLastFetch(new Date(timestamp));
          // Don't set error — we have cached data to show
        } else {
          setError('Unable to load wait times. Please try again later.');
        }
      } catch {
        setError('Unable to load wait times. Please try again later.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      fetchInFlight.current = false;
    }
  }, []);

  // ── Polling with visibility-aware interval ──
  // Pauses/slows polling when the tab is hidden to save API calls.
  // Uses exponential backoff on consecutive errors.
  useEffect(() => {
    // Initial fetch
    fetchData();

    function getInterval() {
      const hidden = document.visibilityState === 'hidden';
      const base = hidden ? HIDDEN_INTERVAL : BASE_INTERVAL;

      // Exponential backoff: 60s → 120s → 240s → 300s (capped)
      if (consecutiveErrors.current > 0) {
        const backoff = Math.min(base * Math.pow(2, consecutiveErrors.current), MAX_BACKOFF);
        return backoff;
      }
      return base;
    }

    function scheduleNext() {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        fetchData(true);
      }, getInterval());
    }

    scheduleNext();

    // Re-schedule when tab visibility changes
    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        // Tab came back — fetch immediately if stale (> 60s since last fetch)
        const now = Date.now();
        const lastTime = lastFetch?.getTime() || 0;
        if (now - lastTime > BASE_INTERVAL) {
          fetchData(true);
        }
      }
      scheduleNext();
    }

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getWaitData = useCallback((rideName) => {
    if (!rideName || Object.keys(waitLookup).length === 0) return null;
    const normalized = normalizeName(rideName);

    // 1. Exact normalized match
    if (waitLookup[normalized]) return waitLookup[normalized];

    // 2. Contains match (both directions, min length to avoid false positives)
    for (const [apiNorm, data] of Object.entries(waitLookup)) {
      if (apiNorm.length > 5 && normalized.length > 5) {
        if (normalized.includes(apiNorm) || apiNorm.includes(normalized)) {
          return data;
        }
      }
    }

    // 3. First-words prefix match
    const ourWords = normalized.split(' ').filter(w => w.length > 2);
    if (ourWords.length >= 2) {
      const prefix = ourWords.slice(0, Math.min(3, ourWords.length)).join(' ');
      for (const [apiNorm, data] of Object.entries(waitLookup)) {
        if (apiNorm.startsWith(prefix)) return data;
      }
    }

    return null;
  }, [waitLookup]);

  const refresh = useCallback(() => fetchData(true), [fetchData]);

  return (
    <WaitTimesContext.Provider value={{
      parksData,
      loading,
      error,
      lastFetch,
      refreshing,
      getWaitData,
      refresh,
    }}>
      {children}
    </WaitTimesContext.Provider>
  );
}
